import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMobile } from '../../context/MobileContext';
import {
  Scale, LayoutDashboard, Briefcase, CheckSquare,
  DollarSign, Calendar, Building2, MessageCircle, FileText,
  Settings, LogOut, Mail, TrendingUp, UserPlus,
  LifeBuoy, ClipboardList, Phone } from 'lucide-react';

export default function Sidebar({ onMessagesClick, unreadCount = 0, taskCount = 0, intakeCount = 0, alertCount = 0 }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { sidebarOpen, closeSidebar, isMobile } = useMobile();

  const allNavItems = [
    { to: '/',          icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/cases',     icon: Briefcase,       label: 'Cases' },
    { to: '/intake',    icon: UserPlus,        label: 'Client Intake' },
    { to: '/tasks',     icon: CheckSquare,     label: 'Tasks' },
    { to: '/billing',   icon: DollarSign,      label: 'Billing',     superAdminOnly: true },
    { to: '/calendar',  icon: Calendar,        label: 'Calendar' },
    { to: '/communications', icon: Phone,     label: 'Communications' },
    { to: '/network',   icon: Building2,       label: 'Provider Network' },
    { to: '/reports',   icon: TrendingUp,      label: 'Reports' },
    { to: '/letters',   icon: Mail,            label: 'Letters & Forms' },
    { to: '/settings',  icon: Settings,        label: 'Settings' },
  ];

  const bottomNavItems = [
    { to: '/audit-log', icon: ClipboardList, label: 'Audit Log', superAdminOnly: true },
    { to: '/support', icon: LifeBuoy, label: 'Support' },
  ];

  const userAccess = user?.access_level || 'standard';
  const navItems = allNavItems.filter(item => !item.superAdminOnly || userAccess === 'super_admin');

  const accessLabel = userAccess === 'super_admin' ? 'Super Admin' : userAccess === 'admin' ? 'Admin' : '';
  const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '';
  const initials = `${(user?.first_name || '')[0] || ''}${(user?.last_name || '')[0] || ''}`.toUpperCase() || '?';
  const logoUrl = user?.organization?.logo_url;

  const handleNavClick = () => {
    if (isMobile) closeSidebar();
  };

  const handleMessagesClick = () => {
    onMessagesClick();
    if (isMobile) closeSidebar();
  };

  return (
    <aside className={`sidebar${isMobile ? (sidebarOpen ? ' sidebar-mobile-open' : ' sidebar-mobile-closed') : ''}`}>
      {/* Logo */}
      <div className="sidebar-logo" onClick={() => { navigate('/'); handleNavClick(); }} style={{ cursor: 'pointer' }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={user?.organization?.name || 'Firm Logo'}
            style={{ maxHeight: '82px', maxWidth: '200px', width: '100%', objectFit: 'contain' }}
          />
        ) : (
          <>
            <div className="logo-icon"><Scale size={20} /></div>
            <span className="logo-text">
              {(() => {
                const name = (user && user.organization && user.organization.name) ? user.organization.name : 'ZeusCMS';
                const words = name.trim().split(/\s+/);
                if (words.length <= 2) {
                  return name;
                }
                const line1 = words.slice(0, 2).join(' ');
                const line2 = words.slice(2).join(' ');
                return (
                  <>
                    <span className="logo-text-main">{line1}</span>
                    <span className="logo-text-rest">{line2}</span>
                  </>
                );
              })()}
            </span>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Icon size={18} />
            <span>{label}</span>
            {label === 'Dashboard' && alertCount > 0 && (
              <span className="message-badge" style={{ background: '#ef4444' }}>{alertCount > 99 ? '99+' : alertCount}</span>
            )}
            {label === 'Tasks' && taskCount > 0 && (
              <span className="message-badge">{taskCount > 99 ? '99+' : taskCount}</span>
            )}
            {label === 'Client Intake' && intakeCount > 0 && (
              <span className="message-badge">{intakeCount > 99 ? '99+' : intakeCount}</span>
            )}
          </NavLink>
        ))}

        {/* Messages */}
        <button
          className="sidebar-link"
          onClick={handleMessagesClick}
          style={{
            width: '100%', border: 'none', cursor: 'pointer',
            background: 'transparent',
            font: 'inherit', textAlign: 'left',
          }}
        >
          <MessageCircle size={18} />
          <span>Messenger</span>
          {unreadCount > 0 && (
            <span className="message-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
      </nav>

      {/* Bottom nav — Support pinned above user */}
      <div className="sidebar-bottom-nav">
        {bottomNavItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={handleNavClick}
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </div>

      {/* User section */}
      <div className="sidebar-user">
        <div
          className="sidebar-avatar"
          onClick={() => { navigate('/profile'); handleNavClick(); }}
          style={{
            cursor: 'pointer',
            background: user?.avatar ? `url(${user.avatar}) center/cover` : undefined,
            color: user?.avatar ? 'transparent' : undefined,
          }}
          title="View Profile"
        >
          {!user?.avatar && initials}
        </div>
        <div className="sidebar-user-info" onClick={() => { navigate('/profile'); handleNavClick(); }} style={{ cursor: 'pointer' }}>
          <div className="sidebar-user-name">{user?.first_name} {user?.last_name}</div>
          <div className="sidebar-user-role">{roleLabel}{accessLabel ? ` · ${accessLabel}` : ''}</div>
        </div>
        <button className="btn-icon" onClick={logout} title="Logout">
          <LogOut size={16} />
        </button>
      </div>
    </aside>
  );
}
